import { DRIVING_GROUPS, DRIVING_LIMITS, LIVE_RULES, checkReportRange, daysInRange, drivingBandLabels, drivingBandShares, type DrivingGroup, type DrivingMetrics, type DrivingResult } from "@fts/core";
import { HelpTip } from "../../components/HelpTip";
import { EmptyState, PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { alarmTypeLabel } from "../../lib/alarms";
import { descendantIds } from "../../lib/fleet";
import { drivingEventColumns, drivingRowLabel, drivingSheet, durationLabel } from "../../lib/report-sheets";
import { navigate, type Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { coverageOf, formatNum, formatPct } from "../../lib/report-figures";
import { ChartPanel, CoverageNote, PrintBrand, ReportContext, ReportEmpty, SummaryCards, periodText, previousOf } from "./ReportParts";
import { drivingChart, drivingSummary, toSummaryLines } from "./presenters";
import { EcoSection } from "./EcoSection";
import { ExportBar, FilterBar, Loading, contextChips, filterSummary, filtersQuery, parseFilters, rangeKey, siteLabel, todayLocal, useLatest, useLoad, settleWithin, PREVIOUS_WAIT_MS, type ExportSpec } from "./shared";

/**
 * Sürüş karakteristiği (ADR 0026): per operator or per vehicle, how the forklifts are driven — moving time,
 * distance, speeds, time in speed bands and safety events — for İSG coaching. Transparent numbers only, no
 * score. Personal data (KVKK): every tenant role that sees reports (owner decision 2026-11-01), the purpose
 * and the sharing prerequisite on screen, every export logged; operators the user may not see are only counted.
 */
export function DrivingReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale, lang } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const group: DrivingGroup = route.query.get("grp") === "vehicle" ? "vehicle" : "operator";
  const issue = checkReportRange(f.from, f.to, DRIVING_LIMITS.maxDays);
  const tenantId = tenant?.id;

  const inSite = f.siteId ? descendantIds(data.sites, f.siteId) : null;
  const scopeVehicles = data.vehicles.filter((v) => (!inSite || inSite.has(v.siteId)) && (f.vehicleIds.length === 0 || f.vehicleIds.includes(v.id)));
  const tooLarge = !issue && scopeVehicles.length * daysInRange(f.from, f.to) > DRIVING_LIMITS.maxVehicleDays;

  const query = (id: string, range = { from: f.from, to: f.to }) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: f.operatorId, ...range, group });
  const deps = [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.operatorId, f.from, f.to, group];
  const state = useLoad<DrivingResult>(tenantId && !issue && !tooLarge ? () => provider.reportDriving(query(tenantId)) : null, deps);
  // The previous equal period through the same report call (same filters, same RLS): the comparison only.
  const prevRange = previousOf(f);
  // Fired only once the main result is in (never two report calls racing for the same screen).
  const prev = useLoad<DrivingResult>(tenantId && !issue && !tooLarge && state.status === "ready" ? () => provider.reportDriving(query(tenantId, prevRange)) : null, [...deps, state.status, state.data]);
  const prevLatest = useLatest(prev);
  const res = state.data;
  const ctx = { t, locale, lang };
  const prevRes = prev.status === "ready" ? prev.data : null;
  const coverage = coverageOf({ vehicles: scopeVehicles, devices: data.devices, from: f.from, to: f.to, tz: res?.tz ?? "Europe/Istanbul", today: todayLocal() });
  const filters = filterSummary(t, data, f);

  const keep = { grp: group === "operator" ? undefined : group };
  const setGroup = (g: DrivingGroup) => navigate("/reports/driving", { ...filtersQuery(f), grp: g === "operator" ? undefined : g });
  const num = (v: number | null, d = 1) => (v === null ? t("common.empty") : formatNum(v, locale, d));
  const pct = (v: number | null) => (v === null ? t("common.empty") : formatPct(v, locale));

  const spec: ExportSpec | null = state.status === "ready" && res && tenantId
    ? {
        report: "driving",
        reportName: `${t("reports.driving.title")} — ${t(`reports.driving.group.${group}`)}`,
        slug: t("reports.driving.slug"),
        part: `${group}_${f.from}_${f.to}`,
        tz: res.tz,
        filters: [[t("reports.driving.group"), t(`reports.driving.group.${group}`)], ...filters, [t("reports.driving.purposeLabel"), t("reports.driving.purpose")]],
        period: periodText(t, lang, f.from, f.to),
        rows: res.rows.length + 1,
        load: async (format) => {
          const [r, p] = await Promise.all([provider.exportDriving(query(tenantId), format), format === "pdf" ? settleWithin(prevLatest.current.promise, PREVIOUS_WAIT_MS) : Promise.resolve(null)]);
          return {
            sheets: [drivingSheet(t, r)],
            tz: r.tz,
            summary: toSummaryLines(ctx, drivingSummary(ctx, r, p)),
            chart: { title: t("reports.driving.chart.title"), spec: drivingChart(ctx, r), note: t("reports.driving.chart.note") },
          };
        },
      }
    : null;

  const rk = issue === "too_long" ? "reports.driving.rangeTooLong" : rangeKey(issue);
  const bands = drivingBandLabels(res?.bandsKmh ?? DRIVING_LIMITS.bandsKmh);
  const others = res ? drivingEventColumns(res) : [];
  const byOp = group === "operator";

  const cells = (m: DrivingMetrics) => (
    <>
      <td className="num">{durationLabel(t, m.movingS)}</td>
      <td className="num">{num(m.distanceM / 1000, 2)}</td>
      <td className="num">{num(m.avgKmh)}</td>
      <td className="num" title={m.speedOutliers > 0 ? t("reports.driving.outliers", { count: m.speedOutliers, max: DRIVING_LIMITS.maxPlausibleKmh }) : undefined}>
        {num(m.maxKmh)}
        {m.speedOutliers > 0 && <span className="muted"> *</span>}
      </td>
      {drivingBandShares(m.bandS).map((s, i) => (
        <td key={`b${i}`} className="num col-hide-md">
          {pct(s)}
        </td>
      ))}
      {m.impacts.map((n, i) => (
        <td key={`i${i}`} className="num">
          {n}
        </td>
      ))}
      {others.map((x) => (
        <td key={x} className="num col-hide-sm">
          {m.events[x] ?? 0}
        </td>
      ))}
      <td className="num">{num(m.eventsPer10h, 2)}</td>
    </>
  );

  return (
    <>
      <PrintBrand title={t("reports.driving.title")} tz={res?.tz ?? null} />
      <PageHeader
        title={t("reports.driving.title")}
        subtitle={t("reports.driving.subtitle")}
        eyebrow={t("nav.group.reports")}
        help={
          <HelpTip topic={t("help.driving.topic")} align="start">
            {t("help.driving", { bands: bands.join(", "), kmh: LIVE_RULES.idleBelowKmh, max: DRIVING_LIMITS.maxPlausibleKmh })}
          </HelpTip>
        }
      />
      <p className="kvkk-note" role="note">
        {t("reports.driving.kvkkNote")}
      </p>
      <div className="heatmap-mode">
        <span className="field-label" id="dr-group-label">
          {t("reports.driving.group")}
        </span>
        <div className="segmented" role="group" aria-labelledby="dr-group-label">
          {DRIVING_GROUPS.map((g) => (
            <button key={g} type="button" className={g === group ? "seg-on" : ""} aria-pressed={g === group} onClick={() => setGroup(g)}>
              {t(`reports.driving.group.${g}`)}
            </button>
          ))}
        </div>
      </div>
      <FilterBar data={data} path="/reports/driving" filters={f} keep={keep} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      {tooLarge && (
        <p className="form-error" role="alert">
          {t("reports.driving.tooLarge", { max: DRIVING_LIMITS.maxVehicleDays.toLocaleString(locale) })}
        </p>
      )}
      <ReportContext chips={[[t("reports.driving.group"), t(`reports.driving.group.${group}`)], ...contextChips(t, filters)]} from={f.from} to={f.to} tz={res?.tz ?? null} sample={provider.mode === "demo"} />
      {state.status === "ready" && res && res.rows.length > 0 && <SummaryCards items={drivingSummary(ctx, res, prevRes)} busy={prev.status === "loading"} prevFrom={prevRange.from} prevTo={prevRange.to} />}
      {state.status === "ready" && <CoverageNote coverage={coverage} tz={res?.tz ?? "Europe/Istanbul"} />}
      <ExportBar spec={spec} disabled={!res || res.rows.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.status === "ready" && res && res.rows.length > 0 && <ChartPanel title={t("reports.driving.chart.title")} spec={drivingChart(ctx, res)} note={t("reports.driving.chart.note")} />}
      <Panel flush meta={state.status === "ready" && res ? <span>{t("reports.rows", { count: res.rows.length })}</span> : undefined}>
        {state.status === "loading" || state.status === "idle" ? (
          rk || tooLarge ? <EmptyState compact icon="info" title={t(rk ?? "reports.driving.narrow")} /> : <Loading />
        ) : !res || res.rows.length === 0 ? (
          state.status === "error" ? null : <ReportEmpty path="/reports/driving" filters={f} siteName={siteLabel(t, data, f.siteId)} keep={keep} />
        ) : (
          <div className="table-wrap table-scroll">
            <table className="table report-table driving-table">
              <thead>
                <tr>
                  <th scope="col">{byOp ? t("reports.col.operator") : t("reports.col.vehicle")}</th>
                  {byOp && <th scope="col" className="num col-hide-sm">{t("reports.driving.col.vehicles")}</th>}
                  <th scope="col" className="num">{t("reports.col.moving")}</th>
                  <th scope="col" className="num">{t("reports.col.distanceKm")}</th>
                  <th scope="col" className="num">{t("reports.driving.col.avgKmh")}</th>
                  <th scope="col" className="num">{t("reports.driving.col.maxKmh")}</th>
                  {bands.map((b) => (
                    <th key={b} scope="col" className="num col-hide-md">
                      {t("reports.driving.col.bandShort", { band: b })}
                    </th>
                  ))}
                  {[1, 2, 3].map((l) => (
                    <th key={l} scope="col" className="num">
                      {t("reports.driving.col.impact", { level: l })}
                    </th>
                  ))}
                  {others.map((x) => (
                    <th key={x} scope="col" className="num col-hide-sm">
                      {alarmTypeLabel(t, x)}
                    </th>
                  ))}
                  <th scope="col" className="num">{t("reports.driving.col.eventsRate", { h: res.rateHours })}</th>
                </tr>
              </thead>
              <tbody>
                {res.rows.map((r) => (
                  <tr key={`${r.kind}:${r.id ?? ""}`}>
                    <td>
                      {r.kind === "vehicle" && r.id ? (
                        <a className="subtle-link mono" href={`#/vehicles/${encodeURIComponent(r.id)}`}>
                          {r.name}
                        </a>
                      ) : r.kind === "operator" && r.id ? (
                        <a className="subtle-link" href={`#/operators/${encodeURIComponent(r.id)}`}>
                          {r.name}
                        </a>
                      ) : (
                        <span className="muted">{drivingRowLabel(t, r)}</span>
                      )}
                    </td>
                    {byOp && <td className="num col-hide-sm">{r.suppressed ? t("common.empty") : r.vehicles}</td>}
                    {r.suppressed ? (
                      // KVKK: one hidden operator is never singled out — the count only.
                      <td className="muted" colSpan={4 + bands.length + 3 + others.length + 1}>
                        {t("reports.driving.suppressed")}
                      </td>
                    ) : (
                      cells(r)
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">{t("reports.totals")}</th>
                  {byOp && <td className="num col-hide-sm">{res.totals.vehicles}</td>}
                  {cells(res.totals)}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">
        {t("reports.driving.rules", { bands: bands.join(", "), max: DRIVING_LIMITS.maxPlausibleKmh, h: DRIVING_LIMITS.rateHours })}{" "}
        {t("reports.driving.notMeasured")}
      </p>
      {/* ADR 0034: eco-driving events of accelerometer units, with their source labelled. */}
      <EcoSection
        query={tenantId && !issue && !tooLarge ? { tenantId, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: f.operatorId } : null}
        group={group}
        filters={filters}
        period={periodText(t, lang, f.from, f.to)}
        from={f.from}
        to={f.to}
      />
    </>
  );
}
